import { RESOURCE_TYPES } from "@eldrun/content";
import { SpatialHash } from "@eldrun/shared";
import type { ResourceSet } from "@eldrun/worldgen";
import { rayCylinder, type CrosshairAim } from "./crosshair-aim.ts";

/** Mesmo alcance do servidor (do centro do jogador à borda do recurso). */
export const CLIENT_REACH = 2.6;
const MAX_ANGLE = (50 * Math.PI) / 180;

export interface Looker {
  x: number;
  y: number;
  z: number;
  yaw: number;
}

/**
 * Escolhe o recurso sob a mira: dentro do alcance, à frente (até 50° do olhar) e preferindo o
 * mais alinhado e mais próximo. O servidor revalida tudo; isto só orienta o jogador.
 */
export class HarvestTargeting {
  private readonly set: ResourceSet;
  private readonly grid = new SpatialHash<number>(8);
  private readonly scratch: number[] = [];
  /** Distância no raio da cruz até o recurso que o último `rayPick` achou. */
  rayT = Infinity;

  constructor(set: ResourceSet) {
    this.set = set;
    for (let k = 0; k < set.count; k++) this.grid.set(k, set.x[k] as number, set.z[k] as number);
  }

  /** Distância do olho do jogador à borda do recurso `k` (para comparar com uma criatura na mira). */
  edgeDistance(looker: Looker, k: number): number {
    const set = this.set;
    const type = RESOURCE_TYPES[set.types[k] as number];
    const radius = (type?.collider?.[0] ?? 0.4) * (set.scale[k] as number);
    return Math.hypot((set.x[k] as number) - looker.x, (set.z[k] as number) - looker.z) - radius;
  }

  /** Recurso de pé mais perto de (x, z), até `r` m, cujo tipo passa no filtro (a seta do tutorial). */
  nearest(x: number, z: number, r: number, accept: (typeIndex: number) => boolean, isRemoved: (id: number) => boolean): { x: number; y: number; z: number } | undefined {
    const set = this.set;
    let best = -1;
    let bestD = r;
    this.scratch.length = 0;
    for (const k of this.grid.queryRadius(x, z, r, this.scratch)) {
      if (!accept(set.types[k] as number) || isRemoved(set.ids[k] as number)) continue;
      const d = Math.hypot((set.x[k] as number) - x, (set.z[k] as number) - z);
      if (d < bestD) {
        bestD = d;
        best = k;
      }
    }
    return best < 0 ? undefined : { x: set.x[best] as number, y: set.y[best] as number, z: set.z[best] as number };
  }

  /** Algum recurso de pé a até `r` m de (x, z)? (o ping "recurso"). */
  near(x: number, z: number, r: number, isRemoved: (id: number) => boolean): boolean {
    const set = this.set;
    this.scratch.length = 0;
    for (const k of this.grid.queryRadius(x, z, r + 2, this.scratch)) {
      if (isRemoved(set.ids[k] as number)) continue;
      const type = RESOURCE_TYPES[set.types[k] as number];
      const radius = (type?.collider?.[0] ?? 0.4) * (set.scale[k] as number);
      if (Math.hypot((set.x[k] as number) - x, (set.z[k] as number) - z) - radius <= r) return true;
    }
    return false;
  }

  /**
   * Recurso sob a cruz (F55): o primeiro que o raio da câmera pega, antes do que o mundo deixa ver
   * (`ray.worldT`), ao alcance do corpo (a mesma regra de `pick`). Índice no conjunto, ou −1.
   */
  rayPick(ray: CrosshairAim, looker: Looker, isRemoved: (id: number) => boolean): number {
    const set = this.set;
    let best = -1;
    let bestT = ray.worldT + 0.05;
    this.scratch.length = 0;
    this.grid.queryRadius(looker.x, looker.z, CLIENT_REACH + 2, this.scratch);
    for (let i = 0; i < this.scratch.length; i++) {
      const k = this.scratch[i] as number;
      if (isRemoved(set.ids[k] as number)) continue;
      const type = RESOURCE_TYPES[set.types[k] as number];
      if (!type) continue;
      const scale = set.scale[k] as number;
      const radius = (type.collider?.[0] ?? 0.4) * scale;
      const x = set.x[k] as number;
      const y = set.y[k] as number;
      const z = set.z[k] as number;
      if (Math.hypot(x - looker.x, z - looker.z) > CLIENT_REACH + radius || Math.abs(y - looker.y) > 4) continue;
      // Sem colisor (moita, galhada), um corpo baixo; com colisor, o tronco ou a rocha dele.
      const top = y + (type.collider?.[1] ?? 1.2) * scale;
      const t = rayCylinder(ray.ox, ray.oy, ray.oz, ray.dx, ray.dy, ray.dz, ray.t0, bestT, x, z, radius, y - 1, top);
      if (t < bestT) {
        bestT = t;
        best = k;
      }
    }
    this.rayT = best < 0 ? Infinity : bestT;
    return best;
  }

  /** Índice no conjunto, ou −1. */
  pick(looker: Looker, isRemoved: (id: number) => boolean): number {
    const set = this.set;
    const fx = -Math.sin(looker.yaw);
    const fz = -Math.cos(looker.yaw);
    let best = -1;
    let bestScore = Infinity;
    this.scratch.length = 0;
    for (const k of this.grid.queryRadius(looker.x, looker.z, CLIENT_REACH + 2, this.scratch)) {
      if (isRemoved(set.ids[k] as number)) continue;
      const type = RESOURCE_TYPES[set.types[k] as number];
      if (!type) continue;
      const radius = (type.collider?.[0] ?? 0.4) * (set.scale[k] as number);
      const dx = (set.x[k] as number) - looker.x;
      const dz = (set.z[k] as number) - looker.z;
      const d = Math.sqrt(dx * dx + dz * dz);
      if (d > CLIENT_REACH + radius || Math.abs((set.y[k] as number) - looker.y) > 4) continue;
      const dot = d > 1e-6 ? (dx * fx + dz * fz) / d : 1;
      const angle = Math.acos(Math.max(-1, Math.min(1, dot)));
      if (angle > MAX_ANGLE && d > radius + 0.5) continue;
      const score = angle + d * 0.08;
      if (score < bestScore) {
        bestScore = score;
        best = k;
      }
    }
    return best;
  }
}
